import React, { useContext } from 'react';
import { Box, Button, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { CDTSegmentType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { formatSegmentType } from 'src/models/segment-cdt/SegmentTableListRow';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { CREATABLE_SEGMENT_TYPES, isValidTableName, segmentCoaName } from 'src/components/segment-cdt/create/segmentCreateUtils';

const SEGMENT_OPTIONS = CREATABLE_SEGMENT_TYPES.map((v) => ({ value: v, label: formatSegmentType(v) }));
const CADENCE_OPTIONS = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
];

/**
 * Wizard Step 1: table name (forced `ATP_CDT_` prefix) + the segment it is built on + optional refresh
 * cadence + optional Segment Population (Custom CoA, captured only). "Next" advances to the columns step.
 */
export default function SegmentMetaStep() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    // Custom CoA definitions are scoped to a segment; fetch them for the chosen one (null until picked,
    // which leaves the dropdown disabled). ENTITY maps to COMPANY via segmentCoaName.
    const [customCoas, coaLoading] = services.customCOAService.getCustomCOADefinitionsForSegment(segmentCoaName(cw.segmentType));

    const setMeta = (patch: any) => dispatch(ACTIONS.SET_DRAFT_META.withPayload(patch));
    const nameTouchedAndInvalid = cw.tableName !== CONSTANTS.CUSTOM_DATA_TABLE_PREFIX && !isValidTableName(cw.tableName);
    const canAdvance = isValidTableName(cw.tableName) && !!cw.segmentType;

    // The ATP_CDT_ prefix is fixed and server-enforced; the user types only the suffix. Accept an edit
    // only while it still starts with the prefix (matching the CLI CreateTableModal); a keystroke that
    // would delete into the prefix is ignored, so the prefix can never be removed or doubled.
    const onNameChange = (value: string) => {
        if (value.indexOf(CONSTANTS.CUSTOM_DATA_TABLE_PREFIX) === 0) {
            setMeta({ tableName: value });
        }
    };

    return (
        <SpaceBetween size="m">
            <Box color="text-body-secondary">
                Define the table name and the segment it is built on. Columns are added in the next step.
            </Box>
            <FormField
                label="Table name"
                description="The ATP_CDT_ prefix is fixed. Add letters, numbers, and underscores after it."
                errorText={nameTouchedAndInvalid ? 'Add letters, numbers, or underscores after the ATP_CDT_ prefix.' : undefined}
            >
                <Input
                    value={cw.tableName}
                    placeholder="ATP_CDT_2D_Royalty_Opex"
                    onChange={({ detail }) => onNameChange(detail.value)}
                />
            </FormField>
            <FormField
                label="Segment to segregate on"
                description="Each row is one value of this segment. This cannot be changed after creation."
                constraintText={cw.columns.length > 0 ? 'To change the segment, first remove the columns you have added. Each column\'s data source is tied to the current segment.' : undefined}
            >
                <Select
                    selectedOption={SEGMENT_OPTIONS.find((o) => o.value === cw.segmentType) ?? null}
                    options={SEGMENT_OPTIONS}
                    // Locked once columns exist: a column's GL pull filter is segment-scoped, so changing the
                    // segment would silently invalidate it. The segment is immutable after creation anyway.
                    disabled={cw.columns.length > 0}
                    placeholder="Select a segment"
                    onChange={({ detail }) => setMeta({ segmentType: detail.selectedOption.value as CDTSegmentType })}
                />
            </FormField>
            <FormField label="Refresh cadence">
                <Select
                    selectedOption={CADENCE_OPTIONS.find((o) => o.value === cw.refreshCadence) ?? null}
                    options={[
                        ...(cw.refreshCadence ? [{ value: '', label: 'None' }] : []),
                        ...CADENCE_OPTIONS,
                    ]}
                    placeholder="Select a cadence (optional)"
                    onChange={({ detail }) => setMeta({ refreshCadence: detail.selectedOption.value || undefined })}
                />
            </FormField>
            <FormField label="Segment Population (Custom CoA)" description="Optional. Restrict which segment rows exist; leave blank for the full population.">
                <Select
                    disabled={!cw.segmentType}
                    statusType={coaLoading ? 'loading' : 'finished'}
                    loadingText="Loading custom CoA definitions"
                    filteringType="auto"
                    selectedOption={
                        cw.segmentCustomCoaId
                            ? { value: cw.segmentCustomCoaId, label: (customCoas ?? []).find((d) => d.tableId === cw.segmentCustomCoaId)?.tableName ?? cw.segmentCustomCoaId }
                            : null
                    }
                    options={[
                        ...(cw.segmentCustomCoaId ? [{ value: '', label: 'None (full population)' }] : []),
                        ...(customCoas ?? []).map((d) => ({ value: d.tableId, label: d.tableName })),
                    ]}
                    placeholder={cw.segmentType ? 'Leave blank for all segments' : 'Select a segment first'}
                    onChange={({ detail }) => setMeta({ segmentCustomCoaId: detail.selectedOption.value || undefined })}
                    empty={`No custom CoA definitions for ${cw.segmentType ? segmentCoaName(cw.segmentType) : 'this segment'}`}
                />
            </FormField>
            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    <Button onClick={() => dispatch(ACTIONS.CLOSE_CREATE_WIZARD)}>Cancel</Button>
                    <Button variant="primary" disabled={!canAdvance} onClick={() => dispatch(ACTIONS.SET_WIZARD_STEP.withPayload(2))}>
                        Next: Add columns
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
